import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check, Paperclip } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";
import { buildWebPage } from "../components/schema";
import { PageHeader } from "../components/PageHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { nome: "", cognome: "", email: "", telefono: "", zona: "", settore: "", messaggio: "" };

const CV_EXTS = [".pdf", ".doc", ".docx"];
const CV_MAX = 5 * 1024 * 1024;

const TARGET = [
  "Privati ed edilizia residenziale",
  "Ho.Re.Ca. (Ristoranti, Hotel, Bar, Strutture ricettive)",
  "Prescrittori e Professionisti (Architetti, Geometri, Interior Designer, Imprese edili)",
];
const REQUISITI = [
  "Esperienza consolidata nella vendita diretta o B2B nel settore serramenti, tendaggi, finiture d'interni, edilizia o arredo giardino",
  "Consolidata rete di contatti o spiccata capacità di prospezione",
  "Automunito con disponibilità a spostamenti nella zona di competenza",
  "Massima serietà e orientamento ai risultati",
];
const OFFERTA = [
  "Provvigioni ai vertici del settore su prodotti ad alto valore aggiunto",
  "Catalogo prodotti Made in Italy su misura",
  "Supporto tecnico dedicato per fattibilità, rilievi e preventivazione",
  "Formazione di prodotto sui sistemi VEPA",
  "Possibilità di zona in esclusiva",
];
const FIELDS = [
  { k: "nome", ph: "Nome *" },
  { k: "cognome", ph: "Cognome *" },
  { k: "email", ph: "Email *", type: "email" },
  { k: "telefono", ph: "Telefono *", type: "tel" },
  { k: "zona", ph: "Zona geografica di interesse *" },
  { k: "settore", ph: "Settore di provenienza *" },
];

const List = ({ items, testid }) => (
  <ul className="space-y-4" data-testid={testid}>
    {items.map((t) => (
      <li key={t} className="flex items-start gap-3 font-light text-foreground/80 leading-relaxed">
        <Check strokeWidth={1.2} className="w-5 h-5 mt-0.5 shrink-0 text-champagne" />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

const Block = ({ eyebrow, title, children, testid }) => (
  <div data-testid={testid}>
    <p className="text-xs uppercase tracking-[0.35em] text-champagne mb-5">{eyebrow}</p>
    {title && <h2 className="font-serif font-light text-2xl sm:text-3xl tracking-tight leading-tight mb-8">{title}</h2>}
    {children}
  </div>
);

const CandidaturaForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [cv, setCv] = useState(null);
  const fileRef = useRef(null);

  const pickCv = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!CV_EXTS.some((x) => f.name.toLowerCase().endsWith(x))) {
      toast.error("Formato non valido. Sono accettati PDF, DOC, DOCX.");
      e.target.value = "";
      return;
    }
    if (f.size > CV_MAX) {
      toast.error("Il file supera la dimensione massima di 5MB.");
      e.target.value = "";
      return;
    }
    setCv(f);
  };
  const inputCls =
    "w-full bg-transparent border-b border-white/30 py-3 text-base font-light text-white caret-white focus:border-white focus:outline-none transition-colors duration-300 placeholder:text-white/50";

  const submit = async (e) => {
    e.preventDefault();
    if (FIELDS.some((f) => !form[f.k].trim())) {
      toast.error("Compila tutti i campi obbligatori.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (cv) fd.append("cv", cv);
      await axios.post(`${API}/candidature`, fd);
      toast.success("Candidatura inviata! Ti contatteremo al più presto.");
      setForm(EMPTY);
      setCv(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(err.response?.status === 400 ? err.response.data.detail : "Invio non riuscito. Riprova più tardi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="candidatura-form" className="space-y-8 max-w-3xl mx-auto">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
        {FIELDS.map((f) => (
          <input
            key={f.k}
            type={f.type || "text"}
            data-testid={`candidatura-input-${f.k}`}
            className={inputCls}
            placeholder={f.ph}
            value={form[f.k]}
            onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
          />
        ))}
      </div>
      <textarea
        data-testid="candidatura-input-messaggio"
        rows={5}
        className={`${inputCls} resize-none`}
        placeholder="Messaggio"
        value={form.messaggio}
        onChange={(e) => setForm({ ...form, messaggio: e.target.value })}
      />
      <div data-testid="candidatura-cv-field">
        <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" onChange={pickCv} className="hidden" id="cv-upload" data-testid="candidatura-input-cv" />
        <label htmlFor="cv-upload" className="flex items-center gap-3 w-full border-b border-white/30 py-3 cursor-pointer font-light text-white/50 hover:text-white transition-colors duration-300" data-testid="candidatura-cv-label">
          <Paperclip strokeWidth={1.2} className="w-4 h-4 shrink-0" />
          <span className={cv ? "text-white" : ""} data-testid="candidatura-cv-filename">{cv ? cv.name : "Allega il tuo CV (PDF, DOC, DOCX – max 5MB)"}</span>
        </label>
      </div>
      <button
        type="submit"
        data-testid="candidatura-submit-button"
        disabled={loading}
        className="group inline-flex items-center gap-3 bg-white text-black px-8 py-4 text-sm uppercase tracking-wide hover:gap-5 transition-all duration-300 disabled:opacity-50"
      >
        {loading ? "Invio in corso..." : "INVIA CANDIDATURA"}
        <ArrowRight strokeWidth={1.2} className="w-4 h-4" />
      </button>
    </form>
  );
};

export default function LavoraConNoi() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div data-testid="lavora-con-noi-page">
      <Seo
        title="Lavora con Noi | VETRO DESIGN ITALIA"
        description="Ricerchiamo Agenti di Commercio e Consulenti di Vendita per sviluppare il mercato delle Vetrate Panoramiche e delle Soluzioni in Vetro Indoor e Outdoor."
        path="/lavora-con-noi"
        jsonLd={buildWebPage("Lavora con Noi · VETRO DESIGN ITALIA", "/lavora-con-noi", "Ricerchiamo Agenti di Commercio e Consulenti di Vendita.", "Lavora con Noi")}
      />
      <Navbar />
      <div className="[&_p]:text-justify [&_li]:text-justify [&_h1]:text-justify [&_h2]:text-justify [&_h3]:text-justify" data-testid="lavora-content">
      <PageHeader
        eyebrow="Lavora con Noi"
        h1="Ricerchiamo Agenti di Commercio e Consulenti di Vendita"
        intro="Sviluppa con noi il mercato delle Vetrate Panoramiche e delle Soluzioni in Vetro Indoor e Outdoor"
      />

      <section className="py-16 md:py-24 px-6 md:px-12" data-testid="lavora-intro">
        <div className="max-w-4xl mx-auto space-y-6 font-light text-foreground/80 leading-relaxed text-base md:text-lg">
          <p>Vetro Design Italia è una realtà specializzata nella progettazione, produzione e installazione di vetrate panoramiche amovibili (VEPA) e soluzioni vetro per Outdoor &amp; Indoor.</p>
          <p>Per rafforzare la nostra presenza commerciale e sviluppare nuove aree di mercato, selezioniamo Agenti di Commercio (Mono e Plurimandatari) e Consulenti di Vendita focalizzati sulla ricerca attiva di nuovi clienti e sullo sviluppo del territorio, dotati di una spiccata motivazione e di comprovate capacità commerciali.</p>
        </div>
      </section>

      <section className="pb-24 md:pb-32 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
          <Block eyebrow="Chi cerchiamo" testid="lavora-chi-cerchiamo">
            <p className="font-light text-foreground/80 leading-relaxed mb-10">
              Professionisti della vendita con spiccata attitudine alla ricerca attiva di nuovi clienti, capacità di networking e sviluppo del territorio.
            </p>
            <h3 className="font-serif font-light text-xl sm:text-2xl mb-6">Target di riferimento</h3>
            <List items={TARGET} testid="lavora-target-list" />
            <h3 className="font-serif font-light text-xl sm:text-2xl mt-12 mb-6">Requisiti</h3>
            <List items={REQUISITI} testid="lavora-requisiti-list" />
          </Block>
          <Block eyebrow="Cosa offriamo" testid="lavora-cosa-offriamo">
            <List items={OFFERTA} testid="lavora-offerta-list" />
          </Block>
        </div>
      </section>

      <section className="py-24 md:py-32 px-6 md:px-12 bg-black text-white" data-testid="lavora-candidatura-section">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight transition-colors duration-300 hover:text-[#FFD700]" data-testid="lavora-cta-title">
            CONTATTACI E INVIA IL TUO CURRICULUM VITAE
          </h2>
          <p className="mt-6 text-lg md:text-xl font-light text-[#4ade80]" data-testid="lavora-cta-green">
            Un'opportunità di crescita professionale ed economica ti aspetta!
          </p>
          <p className="mt-4 font-light text-white/70 leading-relaxed" data-testid="lavora-cta-text">
            Valuteremo il tuo CV e, se in target, ti chiameremo per un incontro conoscitivo.
          </p>
        </div>
        <CandidaturaForm />
      </section>
      </div>
      <Footer />
    </div>
  );
}
